import React, { useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

export default function ResetPasswordPage() {
  const [searchParams]        = useSearchParams();
  const token                  = searchParams.get('token') || '';
  const navigate               = useNavigate();
  const { resetPassword, authLoading, authError, clearError } = useAuth();

  const [password,        setPassword]        = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPw,          setShowPw]          = useState(false);
  const [showConfirmPw,   setShowConfirmPw]   = useState(false);
  const [pwTouched,       setPwTouched]       = useState(false);
  const [done,            setDone]            = useState(false);
  const [localError,      setLocalError]      = useState('');

  // ── Password strength ──────────────────────────────────────
  const pwStrength = (() => {
    if (!password) return { score: 0, label: '', color: '' };
    let score = 0;
    if (password.length >= 8)                              score++;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++;
    if (/[0-9]/.test(password))                           score++;
    if (/[^a-zA-Z0-9]/.test(password))                   score++;
    const levels = [
      { label: 'Weak',   color: '#ef4444' },
      { label: 'Weak',   color: '#ef4444' },
      { label: 'Fair',   color: '#f97316' },
      { label: 'Good',   color: '#eab308' },
      { label: 'Strong', color: '#16a34a' },
    ];
    return { score, ...levels[score] };
  })();

  const handleSubmit = async () => {
    setLocalError('');
    clearError();
    if (!password)                          return setLocalError('Please enter a new password.');
    if (pwStrength.score < 2)              return setLocalError('Password is too weak. Add uppercase, numbers or symbols.');
    if (password !== confirmPassword)      return setLocalError('Passwords do not match.');
    if (!token)                            return setLocalError('Invalid or missing reset token. Please request a new link.');

    const ok = await resetPassword(token, password);
    if (ok) setDone(true);
  };

  const displayError = localError || authError;

  // ── Success state ──────────────────────────────────────────
  if (done) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <div style={{fontSize:56, textAlign:'center', marginBottom:12}}>✅</div>
          <h2 style={styles.heading}>Password Reset!</h2>
          <p style={styles.sub}>Your password has been updated successfully. You can now sign in with your new password.</p>
          <button style={styles.btn} onClick={() => navigate('/')}>
            Sign In →
          </button>
        </div>
      </div>
    );
  }

  // ── Invalid / missing token ────────────────────────────────
  if (!token) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <div style={{fontSize:56, textAlign:'center', marginBottom:12}}>⚠️</div>
          <h2 style={styles.heading}>Invalid Link</h2>
          <p style={styles.sub}>This password reset link is invalid or has expired. Please request a new one.</p>
          <Link to="/" style={{...styles.btn, display:'inline-block', textDecoration:'none', textAlign:'center'}}>
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.card}>

        {/* Header */}
        <div style={{textAlign:'center', marginBottom:24}}>
          <div style={{
            width:56, height:56, borderRadius:'50%',
            background:'linear-gradient(135deg,#1DB954,#16a34a)',
            display:'flex', alignItems:'center', justifyContent:'center',
            fontSize:26, margin:'0 auto 14px',
            boxShadow:'0 4px 14px rgba(29,185,84,.35)',
          }}>🔑</div>
          <h2 style={styles.heading}>Create New Password</h2>
          <p style={styles.sub}>Choose a strong password for your DevBrains account.</p>
        </div>

        {/* New password */}
        <div style={styles.field}>
          <label style={styles.label}>New Password <span style={{color:'#ef4444'}}>*</span></label>
          <div style={{position:'relative'}}>
            <input
              style={styles.input}
              type={showPw ? 'text' : 'password'}
              placeholder="Create a strong password"
              value={password}
              onChange={e => { setPassword(e.target.value); setPwTouched(true); setLocalError(''); clearError(); }}
              onKeyDown={e => e.key === 'Enter' && handleSubmit()}
            />
            <button type="button" onClick={() => setShowPw(v => !v)} style={styles.eyeBtn}>
              {showPw ? '🙈' : '👁'}
            </button>
          </div>

          {/* Strength bar */}
          {pwTouched && password && (
            <div style={{marginTop:8}}>
              <div style={{display:'flex', gap:4, marginBottom:4}}>
                {[1,2,3,4].map(i => (
                  <div key={i} style={{
                    flex:1, height:4, borderRadius:4, transition:'background .2s',
                    background: i <= pwStrength.score ? pwStrength.color : '#e5e7eb',
                  }}/>
                ))}
              </div>
              <div style={{display:'flex', justifyContent:'space-between'}}>
                <span style={{fontSize:'.7rem', fontWeight:700, color:pwStrength.color}}>{pwStrength.label}</span>
                <span style={{fontSize:'.68rem', color:'#9ca3af'}}>
                  {[
                    password.length >= 8        && '8+ chars',
                    /[A-Z]/.test(password)      && 'Uppercase',
                    /[0-9]/.test(password)      && 'Number',
                    /[^a-zA-Z0-9]/.test(password) && 'Symbol',
                  ].filter(Boolean).join(' · ')}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Confirm password */}
        <div style={styles.field}>
          <label style={styles.label}>Confirm Password <span style={{color:'#ef4444'}}>*</span></label>
          <div style={{position:'relative'}}>
            <input
              style={{
                ...styles.input,
                borderColor: confirmPassword
                  ? confirmPassword === password ? '#86efac' : '#fca5a5'
                  : undefined,
              }}
              type={showConfirmPw ? 'text' : 'password'}
              placeholder="Re-enter your password"
              value={confirmPassword}
              onChange={e => { setConfirmPassword(e.target.value); setLocalError(''); clearError(); }}
              onKeyDown={e => e.key === 'Enter' && handleSubmit()}
            />
            <button type="button" onClick={() => setShowConfirmPw(v => !v)} style={styles.eyeBtn}>
              {showConfirmPw ? '🙈' : '👁'}
            </button>
          </div>
          {confirmPassword && (
            <p style={{fontSize:'.72rem', marginTop:4, fontWeight:600,
              color: confirmPassword === password ? '#16a34a' : '#ef4444'}}>
              {confirmPassword === password ? '✓ Passwords match' : '✗ Passwords do not match'}
            </p>
          )}
        </div>

        {displayError && (
          <div style={{
            padding:'10px 14px', borderRadius:10,
            background:'#fef2f2', border:'1px solid #fecaca',
            color:'#dc2626', fontSize:'.84rem', fontWeight:600, marginBottom:8,
          }}>
            ⚠️ {displayError}
          </div>
        )}

        <button
          style={{...styles.btn, opacity: authLoading ? .6 : 1, cursor: authLoading ? 'not-allowed' : 'pointer'}}
          onClick={handleSubmit}
          disabled={authLoading}
        >
          {authLoading ? '⏳ Resetting…' : '🔐 Reset Password'}
        </button>

        <div style={{textAlign:'center', marginTop:14}}>
          <Link to="/" style={{fontSize:'.78rem', color:'#6b7280', fontWeight:600, textDecoration:'none'}}>
            ← Back to Sign In
          </Link>
        </div>

      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #f0fdf4 0%, #f9fafb 100%)',
    padding: '24px 16px',
  },
  card: {
    background: '#fff',
    borderRadius: 20,
    padding: '36px 32px',
    width: '100%',
    maxWidth: 420,
    boxShadow: '0 12px 40px rgba(0,0,0,.1)',
  },
  heading: {
    fontSize: '1.3rem',
    fontWeight: 900,
    color: '#111827',
    margin: '0 0 6px',
    textAlign: 'center',
  },
  sub: {
    fontSize: '.84rem',
    color: '#6b7280',
    lineHeight: 1.6,
    textAlign: 'center',
    margin: '0 0 24px',
  },
  field: {
    marginBottom: 16,
  },
  label: {
    display: 'block',
    fontSize: '.82rem',
    fontWeight: 700,
    color: '#374151',
    marginBottom: 5,
  },
  input: {
    width: '100%',
    padding: '11px 44px 11px 14px',
    border: '1.5px solid #e5e7eb',
    borderRadius: 10,
    fontSize: '.875rem',
    color: '#111827',
    background: '#fafafa',
    outline: 'none',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
  },
  eyeBtn: {
    position: 'absolute',
    right: 12,
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: '#9ca3af',
    padding: 2,
    fontSize: '1rem',
  },
  btn: {
    width: '100%',
    padding: '13px',
    border: 'none',
    borderRadius: 10,
    background: 'linear-gradient(135deg, #1DB954, #16a34a)',
    color: '#fff',
    fontSize: '.9rem',
    fontWeight: 800,
    cursor: 'pointer',
    boxShadow: '0 4px 14px rgba(29,185,84,.35)',
    transition: 'opacity .15s',
    marginTop: 4,
  },
};
